'use client'

import { NumberField as NumberFieldPrimitive } from '@base-ui/react/number-field'
import { cva, type VariantProps } from 'class-variance-authority'
import { createContext, useContext, useMemo } from 'react'

import { cn } from '@/lib/utils'

const numberFieldVariants = cva(
  'flex w-full min-w-0 items-stretch overflow-hidden border outline-none transition-[color,box-shadow] focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50 group-data-disabled/number-field:bg-input/10 group-data-invalid/number-field:border-destructive group-data-invalid/number-field:ring-3 group-data-invalid/number-field:ring-destructive/20 dark:group-data-invalid/number-field:border-destructive/50 dark:group-data-invalid/number-field:ring-destructive/40',
  {
    variants: {
      variant: {
        default: 'border-input bg-background dark:bg-input/30',
        outline:
          'border-border bg-background dark:border-input dark:bg-input/30',
        ghost:
          'border-transparent bg-transparent shadow-none focus-within:bg-muted/40 dark:focus-within:bg-muted/20',
      },
      size: {
        sm: 'h-8 rounded-[min(var(--radius-md),10px)]',
        default: 'h-10 rounded-lg',
        lg: 'h-11 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

const numberFieldInputVariants = cva(
  'z-10 min-w-0 flex-1 border-0 bg-transparent text-center tabular-nums text-foreground outline-none ring-0 placeholder:text-muted-foreground focus-visible:ring-0 disabled:cursor-not-allowed',
  {
    variants: {
      size: {
        sm: 'h-8 px-2 text-sm',
        default: 'h-10 px-2 text-base md:text-base',
        lg: 'h-11 px-2.5 text-lg',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const numberFieldTriggerVariants = cva(
  'z-10 inline-flex shrink-0 cursor-pointer items-center justify-center border-0 bg-transparent text-muted-foreground outline-none transition-colors hover:bg-muted hover:text-foreground focus-visible:z-20 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset aria-disabled:pointer-events-none aria-disabled:opacity-50 data-disabled:pointer-events-none data-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      size: {
        sm: 'w-7 [&_svg:not([class*=size-])]:size-3.5',
        default: 'w-9 [&_svg:not([class*=size-])]:size-4',
        lg: 'w-10 [&_svg:not([class*=size-])]:size-4',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

const numberFieldScrubAreaVariants = cva(
  'flex w-full cursor-ew-resize select-none items-center gap-1.5 text-muted-foreground',
  {
    variants: {
      size: {
        sm: 'text-xs',
        default: 'text-sm',
        lg: 'text-base',
      },
    },
    defaultVariants: {
      size: 'default',
    },
  }
)

type NumberFieldStyleContextValue = {
  variant: NonNullable<VariantProps<typeof numberFieldVariants>['variant']>
  size: NonNullable<VariantProps<typeof numberFieldVariants>['size']>
}

const NumberFieldStyleContext = createContext<NumberFieldStyleContextValue>({
  variant: 'default',
  size: 'default',
})

function useNumberFieldStyle() {
  return useContext(NumberFieldStyleContext)
}

function NumberFieldRoot({
  className,
  variant = 'default',
  size = 'default',
  ...props
}: NumberFieldPrimitive.Root.Props & VariantProps<typeof numberFieldVariants>) {
  const styleValue = useMemo((): NumberFieldStyleContextValue => {
    return {
      variant: variant ?? 'default',
      size: size ?? 'default',
    }
  }, [variant, size])
  return (
    <NumberFieldStyleContext.Provider value={styleValue}>
      <NumberFieldPrimitive.Root
        className={cn(
          'group/number-field flex w-full flex-col gap-1.5 outline-none data-disabled:pointer-events-none data-disabled:opacity-50',
          className
        )}
        data-slot="number-field"
        {...props}
      />
    </NumberFieldStyleContext.Provider>
  )
}

function NumberFieldGroup({ className, ...props }: NumberFieldPrimitive.Group.Props) {
  const { variant, size } = useNumberFieldStyle()
  return (
    <NumberFieldPrimitive.Group
      className={cn(numberFieldVariants({ variant, size }), className)}
      data-slot="number-field-group"
      {...props}
    />
  )
}

function NumberFieldInput({ className, ...props }: NumberFieldPrimitive.Input.Props) {
  const { size } = useNumberFieldStyle()
  return (
    <NumberFieldPrimitive.Input
      className={cn(numberFieldInputVariants({ size }), className)}
      data-slot="number-field-input"
      {...props}
    />
  )
}

function NumberFieldDecrement({ className, ...props }: NumberFieldPrimitive.Decrement.Props) {
  const { size } = useNumberFieldStyle()
  return (
    <NumberFieldPrimitive.Decrement
      className={cn(
        numberFieldTriggerVariants({ size }),
        'border-r border-input rounded-none',
        className
      )}
      data-slot="number-field-decrement"
      type="button"
      {...props}
    />
  )
}

function NumberFieldIncrement({ className, ...props }: NumberFieldPrimitive.Increment.Props) {
  const { size } = useNumberFieldStyle()
  return (
    <NumberFieldPrimitive.Increment
      className={cn(
        numberFieldTriggerVariants({ size }),
        'border-l border-input rounded-none',
        className
      )}
      data-slot="number-field-increment"
      type="button"
      {...props}
    />
  )
}

function NumberFieldScrubArea({ className, ...props }: NumberFieldPrimitive.ScrubArea.Props) {
  const { size } = useNumberFieldStyle()
  return (
    <NumberFieldPrimitive.ScrubArea
      className={cn(numberFieldScrubAreaVariants({ size }), className)}
      data-slot="number-field-scrub-area"
      {...props}
    />
  )
}

function NumberFieldScrubAreaCursor({
  className,
  ...props
}: NumberFieldPrimitive.ScrubAreaCursor.Props) {
  return (
    <NumberFieldPrimitive.ScrubAreaCursor
      className={cn('inline-flex text-muted-foreground', className)}
      data-slot="number-field-scrub-area-cursor"
      {...props}
    />
  )
}

const NumberField = {
  Root: NumberFieldRoot,
  Group: NumberFieldGroup,
  Input: NumberFieldInput,
  Increment: NumberFieldIncrement,
  Decrement: NumberFieldDecrement,
  ScrubArea: NumberFieldScrubArea,
  ScrubAreaCursor: NumberFieldScrubAreaCursor,
}

export {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberFieldRoot,
  NumberFieldScrubArea,
  NumberFieldScrubAreaCursor,
  numberFieldInputVariants,
  numberFieldScrubAreaVariants,
  numberFieldTriggerVariants,
  numberFieldVariants,
}
